import { type Node } from '@xyflow/react';

import { VALUE_CHAIN_MAP } from '../../../../../../constants/general';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from '../../../../../../constants/interfaces';
import { getUnitBusinessUnitType, getUnitEstimated, getUnitHeight } from '../../../../../../utils/reactFlowHelpers';
import { isDescendantOfUnit } from './customUnitNodeHelpers';

import type { TBusinessUnitType, TValueChainUnitType } from '../../../../../../constants/interfaces';
import type { IValueChainRecord } from '../../../../../../store/slices/valueChainMap/interfaces/IValueChainRecord';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';

const applyUnitCollapseToNodes = (prevNodes: Node[], nodeId: string): Node[] => {
  const targetNode = prevNodes.find((node) => node.id === nodeId);

  if (!targetNode) {
    return prevNodes;
  }

  const currentIsCollapsed = Boolean((targetNode.data as { isCollapsed?: boolean }).isCollapsed);
  const nextIsCollapsed = !currentIsCollapsed;

  const targetData = targetNode.data as {
    type: TValueChainUnitType,
    businessUnitType?: TBusinessUnitType,
    estimated?: boolean,
    records?: IValueChainRecord[] | null,
    totalChildrenHeight?: number,
    swimlaneId?: string,
    parentId?: string,
  };

  const baseHeight = getUnitHeight(
    targetData.type,
    getUnitBusinessUnitType(targetData),
    getUnitEstimated(targetData),
  );
  const childrenHeight = Number(targetData.totalChildrenHeight) || 0;

  let expandedVisualHeight = baseHeight;

  if (childrenHeight > 0) {
    expandedVisualHeight += VALUE_CHAIN_MAP.UNIT_RECORD_PADDING
      + childrenHeight
      + VALUE_CHAIN_MAP.UNIT_CONTAINER_MARGIN_BOTTOM;
  }

  const collapsedHeightApprox = VALUE_CHAIN_MAP.CORRECTED_PROFIT_CENTER_HEIGHT;
  const heightDelta = Math.max(expandedVisualHeight - collapsedHeightApprox, 0);
  const signedDelta = nextIsCollapsed ? -heightDelta : heightDelta;

  const nodesById = new Map<string, Node>(
    prevNodes.map((unitItem) => [unitItem.id, unitItem]),
  );

  const getAncestorUnits = (unitNode: Node): Node[] => {
    const ancestors: Node[] = [];
    let currentParentId = unitNode.parentId;

    while (currentParentId && currentParentId.startsWith('unit_')) {
      const parentNode = nodesById.get(currentParentId);

      if (!parentNode || parentNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
        break;
      }

      ancestors.push(parentNode);
      currentParentId = parentNode.parentId;
    }

    return ancestors;
  };

  const ancestorUnits = getAncestorUnits(targetNode);
  const affectedAncestors = [targetNode, ...ancestorUnits];

  return prevNodes.map((node) => {
    if (node.id === nodeId) {
      return {
        ...node,
        data: {
          ...node.data,
          isCollapsed: nextIsCollapsed,
        },
      };
    }

    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT && isDescendantOfUnit(prevNodes, node.id, nodeId)) {
      if (nextIsCollapsed) {
        return {
          ...node,
          data: {
            ...node.data,
            isHiddenByParent: true,
          },
        };
      }

      let scanParentId = node.parentId;
      let hasCollapsedAncestorBetween = false;

      while (scanParentId && scanParentId.startsWith('unit_')) {
        if (scanParentId === nodeId) {
          break;
        }

        const ancestorNode = nodesById.get(scanParentId);

        if (!ancestorNode) {
          break;
        }

        if ((ancestorNode.data as unknown as ICustomUnitNodeData).isCollapsed) {
          hasCollapsedAncestorBetween = true;
          break;
        }

        scanParentId = ancestorNode.parentId;
      }

      if (hasCollapsedAncestorBetween) {
        return node;
      }

      return {
        ...node,
        data: {
          ...node.data,
          isHiddenByParent: false,
        },
      };
    }

    if (
      signedDelta !== 0
      && node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT
      && isDescendantOfUnit(prevNodes, nodeId, node.id)
    ) {
      const prevTotalChildrenHeight = Number(
        (node.data as { totalChildrenHeight?: number }).totalChildrenHeight,
      ) || 0;

      return {
        ...node,
        data: {
          ...node.data,
          totalChildrenHeight: Math.max(prevTotalChildrenHeight + signedDelta, 0),
        },
      };
    }

    if (
      heightDelta !== 0
      && node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT
      && node.data?.swimlaneId === targetData.swimlaneId
    ) {
      const shouldShiftForAnyAncestor = affectedAncestors.some((ancestor) => (
        ancestor.id !== node.id
        && ancestor.parentId === node.parentId
        && (ancestor.data as { swimlaneId?: string }).swimlaneId === node.data?.swimlaneId
        && node.position.y > ancestor.position.y
      ));

      if (!shouldShiftForAnyAncestor) {
        return node;
      }

      return {
        ...node,
        position: {
          ...node.position,
          y: node.position.y + signedDelta,
        },
      };
    }

    return node;
  });
};

export default applyUnitCollapseToNodes;
